/**
 * Director name / ID type / IC fields for Malaysia Directors Report vs Statement by Directors.
 * Kept in one module for useValidation + useDirectorCrossSectionSuggest (single source of truth).
 */

export const DIRECTOR_ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth'] as const;
export type DirectorOrdinal = (typeof DIRECTOR_ORDINALS)[number];

export type DirectorStatementSlot = {
  name: string;
  type: string;
  id: string;
  disclosure: string;
};

export type DirectorReportSlot = {
  name: string;
  type: string;
  id: string;
};

/** Statement by Directors — field names (matches taxonomy / useValidation). */
export const DIRECTOR_FIELDS_STATEMENT: Record<DirectorOrdinal, DirectorStatementSlot> = {
  First: {
    name: 'NameOfFirstDirectorWhoSignedStatementByDirectors',
    type: 'TypeOfIdentificationOfFirstDirectorWhoSignedStatementByDirectors',
    id: 'IdentificationNumberOfFirstDirectorWhoSignedStatementByDirectors',
    disclosure: 'DisclosureWhetherFirstDirectorIsAlsoPrimarilyResponsibleForFinancialManagementOfCompany'
  },
  Second: {
    name: 'NameOfSecondDirectorWhoSignedStatementByDirectors',
    type: 'TypeOfIdentificationNumberOfSecondDirectorWhoSignedStatementByDirectors',
    id: 'IdentificationNumberOfSecondDirectorWhoSignedStatementByDirectors',
    disclosure: 'DisclosureWhetherSecondDirectorIsAlsoPrimarilyResponsibleForFinancialManagementOfCompany'
  },
  Third: {
    name: 'NameOfThirdDirectorWhoSignedStatementByDirectors',
    type: 'TypeOfIdentificationOfThirdDirectorWhoSignedStatementByDirectors',
    id: 'IdentificationNumberOfThirdDirectorWhoSignedStatementByDirectors',
    disclosure: 'DisclosureWhetherThirdDirectorIsAlsoPrimarilyResponsibleForFinancialManagementOfTheCompany'
  },
  Fourth: {
    name: 'NameOfFourthDirectorWhoSignedStatementByDirectors',
    type: 'TypeOfIdentificationOfFourthDirectorWhoSignedStatementByDirectors',
    id: 'IdentificationNumberOfFourthDirectorWhoSignedStatementByDirectors',
    disclosure: 'DisclosureWhetherFourthDirectorIsAlsoPrimarilyResponsibleForFinancialManagementOfCompany'
  },
  Fifth: {
    name: 'NameOfFifthDirectorWhoSignedStatementByDirectors',
    type: 'TypeOfIdentificationOfFifthDirectorWhoSignedStatementByDirectors',
    id: 'IdentificationNumberOfFifthDirectorWhoSignedStatementByDirectors',
    disclosure: 'DisclosureWhetherFifthDirectorIsAlsoPrimarilyResponsibleForFinancialManagementOfCompany'
  }
};

/** Directors Report — field names. */
export const DIRECTOR_FIELDS_REPORT: Record<DirectorOrdinal, DirectorReportSlot> = {
  First: {
    name: 'NameOfFirstDirectorWhoSignedDirectorsReport',
    type: 'TypeOfIdentificationOfFirstDirectorWhoSignedDirectorsReport',
    id: 'IdentificationNumberOfFirstDirectorWhoSignedDirectorsReport'
  },
  Second: {
    name: 'NameOfSecondDirectorWhoSignedDirectorsReport',
    type: 'TypeOfIdentificationOfSecondDirectorWhoSignedDirectorsReport',
    id: 'IdentificationNumberOfSecondDirectorWhoSignedDirectorsReport'
  },
  Third: {
    name: 'NameOfThirdDirectorWhoSignedDirectorsReport',
    type: 'TypeOfIdentificationOfThirdDirectorWhoSignedDirectorsReport',
    id: 'IdentificationNumberOfThirdDirectorWhoSignedDirectorsReport'
  },
  Fourth: {
    name: 'NameOfFourthDirectorWhoSignedDirectorsReport',
    type: 'TypeOfIdentificationOfFourthDirectorWhoSignedDirectorsReport',
    id: 'IdentificationNumberOfFourthDirectorWhoSignedDirectorsReport'
  },
  Fifth: {
    name: 'NameOfFifthDirectorWhoSignedDirectorsReport',
    type: 'TypeOfIdentificationOfFifthDirectorWhoSignedDirectorsReport',
    id: 'IdentificationNumberOfFifthDirectorWhoSignedDirectorsReport'
  }
};

export function normalizeDirectorDisplayName(value: string): string {
  return value.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Resolve First..Fifth from any director row field name. */
export function ordinalFromDirectorFieldName(fieldName: string): DirectorOrdinal | null {
  for (const o of DIRECTOR_ORDINALS) {
    if (fieldName.includes(`${o}Director`)) {
      return o;
    }
  }
  return null;
}

export function sectionFromDirectorFieldName(fieldName: string): 'statement' | 'report' | null {
  if (fieldName.includes('StatementByDirectors')) return 'statement';
  if (fieldName.includes('DirectorsReport')) return 'report';
  return null;
}
